import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTechnicianAppointments } from '../../../entities/appointment'
import { useTimelineWeek } from '../model/useTimelineWeek'
import { groupAppointmentsByDay, groupTimeManagementByDay, groupTasksByDay, filterSchedulableAppointments, filterSchedulableTimeManagement, filterSchedulableTasks } from '../../../shared/lib/timeline/weekHelpers'
import { useTimelineTasks } from '../../../entities/task'
import { routes } from '../../../shared/config/routes'
import { WeekHeader } from './WeekHeader'
import { WeekTechnicianRow } from './WeekTechnicianRow'
import { WeekTechnicianRowSkeleton } from './WeekTechnicianRowSkeleton'
import styles from './Timeline.module.css'

interface Props {
  onDayClick: (dayKey: string) => void
}

export const TimeLineWeekWidget = ({ onDayClick }: Props) => {
  const { weekDays, from, to } = useTimelineWeek()
  const { data, isLoading, isFetching } = useTechnicianAppointments({ from, to })
  const allTasks = useTimelineTasks(from, to)

  const navigate = useNavigate()
  const handleTaskClick = useCallback((taskId: number) => {
    navigate(routes.aufgaben.edit(taskId))
  }, [navigate])

  const handleTimeManagementClick = useCallback((id: number) => {
    navigate(routes.zeitmanagement.edit(id))
  }, [navigate])

  const SKELETON_COUNT = 5

  return (
    <div className="relative w-full flex flex-col gap-px overflow-x-auto">
      <WeekHeader weekDays={weekDays} />
      {isLoading && Array.from({ length: SKELETON_COUNT }).map((_, i) => (
        <WeekTechnicianRowSkeleton key={i} index={i} />
      ))}
      {isFetching && !isLoading && (
        <div className={styles.fetchingOverlay} />
      )}
      {!isLoading && data?.technicians.length === 0 && (
        <div className="flex items-center justify-center py-16 text-sm text-gray-400">
          Keine Handwerker gefunden
        </div>
      )}
      {data?.technicians.map((item, index) => {
        const byDay = groupAppointmentsByDay(filterSchedulableAppointments(item.appointments), weekDays)
        const tmByDay = groupTimeManagementByDay(filterSchedulableTimeManagement(item.timeManagementEntries ?? []), weekDays)
        const techTasks = allTasks.filter(
          (t) => t.assignedTechnician && String(t.assignedTechnician.id) === item.technicianId,
        )
        const tasksByDay = groupTasksByDay(filterSchedulableTasks(techTasks), weekDays)

        return (
          <div key={item.technicianId} className={styles.rowEnter} style={{ animationDelay: `${index * 40}ms` }}>
            <WeekTechnicianRow
              item={item}
              weekDays={weekDays}
              appointmentsByDay={byDay}
              timeManagementByDay={tmByDay}
              tasksByDay={tasksByDay}
              onDayClick={onDayClick}
              onTimeManagementClick={handleTimeManagementClick}
              onTaskClick={handleTaskClick}
            />
          </div>
        )
      })}
    </div>
  )
}
